import type { CollectionDTO } from "../api/client";
import { RowActions } from "@/shared/ui/RowActions";
import { DataTable, type TanstackReactTable } from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";

function nameLo(row: CollectionDTO): string {
  return (row.name as { lo?: string })?.lo ?? "—";
}

type CollectionsTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: CollectionDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onEdit: (row: CollectionDTO) => void;
  onDelete: (id: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function CollectionsTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onEdit,
  onDelete,
  onPaginationChange,
  onSortingChange,
}: CollectionsTableProps) {
  const columns: TanstackReactTable.ColumnDef<CollectionDTO>[] = [
    {
      id: "nameLo",
      header: "ຊື່ (ລາວ)",
      cell: ({ row }) => nameLo(row.original),
    },
    {
      id: "nameTh",
      header: "ຊື່ (ໄທ)",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {(row.original.name as { th?: string })?.th ?? "—"}
        </span>
      ),
    },
    {
      id: "nameEn",
      header: "ຊື່ (ອັງກິດ)",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {(row.original.name as { en?: string })?.en ?? "—"}
        </span>
      ),
    },
    {
      id: "slug",
      header: "Slug",
      cell: ({ row }) => row.original.slug,
    },
    {
      id: "sortOrder",
      header: "ລຳດັບ",
      cell: ({ row }) => row.original.sortOrder,
    },
    {
      id: "actions",
      cell: ({ row }) => {
        if (!canManage) return null;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: "ລຶບ",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: () => onDelete(row.original.id),
            variant: "destructive" as const,
          },
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <div data-tourid="catalog-collections-table">
      <DataTable<CollectionDTO, unknown>
        noDataMessage="ບໍ່ພົບ Collection"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(s) => {
          if (s[0]?.id === "") return;
          onSortingChange(s[0]?.id || "", s[0]?.desc || false);
        }}
      />
    </div>
  );
}
